import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { progressApi, studentPointsQuery } from '@/api/progress';
import { curatorApi } from '@/api/users';
import type { ApiError } from '@/api/client';
import { humanizeError } from '@/lib/errors';
import { pointsRows } from '@/lib/studentPoints';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MLabel, MRow, MSection, MStats, MTitle } from '@/components/mobile/MobileKit';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Pill } from '@/components/ui/Pill';
import { StudentDevicesCard } from '@/components/staff/StudentDevicesCard';
import { StudentPointsCard } from '@/components/staff/StudentPointsCard';

/**
 * Read-only student profile as seen by their curator. Backed by
 * GET /curator/students/:id (CuratorStudentView) — a curator-scoped endpoint
 * with an IDOR guard, unlike the admin detail page which hits /admin/users/:id
 * and 404s for curators. Curators get no admin actions (block/delete/creds).
 *
 * ⚠️ БАЛЛЫ БЕРУТСЯ У PROGRESS-SERVICE, А НЕ ИЗ ЭТОГО ОТВЕТА (12.09.2026, договор
 * `student-points.md`). users-service больше не присылает ни `points`, ни
 * `current_level`: первое — колонка, которую не пишет ни одна служба, второе —
 * одинаковое `A1` у 2056 профилей из 2058. «Уровень» убран с экрана совсем,
 * решение владельца: вернём, когда его начнут измерять.
 */
export function CuratorStudentDetailPage() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();

  const student = useQuery({
    queryKey: ['curator', 'student', userId],
    queryFn: () => curatorApi.getStudent(userId!),
    enabled: !!userId,
  });
  // Тот же ключ, что у карточки баллов ниже, — запрос уходит один.
  const points = useQuery(studentPointsQuery(userId));
  const isMobile = useIsMobile();
  // «Курсов» на телефоне — по ручке прогресса, а не по строкам баллов: курсы с нулём баллов в ответе баллов не
  // приходят (student-points.md §4), и число соврало бы. Ключ — тот же, что у экрана «Прогресс», куда ведёт кнопка.
  const lessons = useQuery({
    queryKey: ['staff', 'student-lessons', userId],
    queryFn: () => progressApi.getStudentLessons(userId!),
    enabled: !!userId && isMobile,
  });

  const back = (
    <Link
      to="/curator/students"
      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
    >
      <span className="material-symbols-outlined text-[18px]">arrow_back</span>
      {t('curatorStudentDetail.back')}
    </Link>
  );

  if (student.isLoading) {
    return (
      <div className="space-y-4">
        {back}
        <Card variant="subtle">{t('curatorStudentDetail.loading')}</Card>
      </div>
    );
  }

  if (student.isError || !student.data) {
    return (
      <div className="space-y-4">
        {back}
        <Card variant="subtle" className="text-center text-error">
          {student.isError ? humanizeError(student.error) : t('curatorStudentDetail.notFound')}
        </Card>
      </div>
    );
  }

  const s = student.data;
  const name = s.full_name || t('curatorStudents.studentFallback');
  const fmtDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.4) ──────────────────────────
  // Картины навыков здесь пока нет: ручка для куратора — заход B (§6), блок встанет после «Баллов» шагом 10.
  if (isMobile) {
    const rows = pointsRows(points.data);
    const shortDate = (iso?: string) =>
      iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
    return (
      <div className="space-y-3">
        <MTitle
          back="/curator/students"
          title={name}
          subtitle={s.email || t('curatorStudents.studentCode', { code: (s.user_id ?? '').slice(0, 8) })}
        />
        <div className="flex gap-2">
          {/* Подпись в одну строку: на 390 «Прогресс ученика» и «Написать в чате» при 14 px и полях 16 px
              переносились в две (кадр стенда 13.10.2026). Не влезает в другом языке — многоточие, а не перенос. */}
          <Link
            to={`/curator/students/${userId}/progress`}
            className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 text-[13px] font-bold text-on-primary"
          >
            <span className="material-symbols-outlined flex-none text-[18px]">trending_up</span>
            <span className="truncate">{t('studentProgress.open')}</span>
          </Link>
          {/* Сразу диалог с этим учеником, а не список чатов (§4.4): чат открывает его по ?student=. */}
          <Link
            to={`/curator/chat?student=${userId}`}
            className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-surface-container-low px-3 text-[13px] font-semibold text-on-surface"
          >
            <span className="material-symbols-outlined flex-none text-[18px]">chat</span>
            <span className="truncate">{t('curatorStudentDetail.openChat')}</span>
          </Link>
        </div>
        <MStats
          cols={3}
          items={[
            { value: points.data ? points.data.total_points : '—', label: t('curatorStudentDetail.points'), tone: 'warm' },
            { value: shortDate(s.last_activity_at), label: t('curatorStudentDetail.lastActivity') },
            { value: lessons.data ? lessons.data.marathons.length : '—', label: t('curatorStudentDetail.courses') },
          ]}
        />

        <MSection title={t('studentPoints.title')} />
        {points.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : points.isError ? (
          // Ошибка — ошибкой, а не нулём (StudentPointsCard): сбой чтения не должен выглядеть учеником без баллов.
          <p role="alert" className="px-0.5 text-[13px] text-error">
            {(points.error as unknown as ApiError | null)?.status === 403
              ? t('studentPoints.forbidden')
              : humanizeError(points.error)}
          </p>
        ) : !rows.length ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('studentPoints.empty')}</p>
        ) : (
          <MBlock>
            {rows.map((row, i) => (
              <MRow
                key={row.id}
                wrap
                title={row.kind === 'outside' ? t('studentPoints.outside') : row.title || t('studentPoints.courseGone')}
                meta={
                  row.kind === 'outside'
                    ? t('studentPoints.outsideHint')
                    : row.deleted
                      ? t('studentPoints.courseDeleted')
                      : undefined
                }
                trailing={
                  <span className="flex-none text-[13px] font-semibold tabular-nums text-on-surface">
                    {t('curatorStudents.points', { count: row.points })}
                  </span>
                }
                last={i === rows.length - 1}
              />
            ))}
          </MBlock>
        )}

        <StudentDevicesCard studentId={userId!} />

        {(s.goals || s.bio) && (
          <div>
            {s.goals && (
              <>
                <MLabel>{t('curatorStudentDetail.goals')}</MLabel>
                <p className="px-0.5 text-[13px] text-on-surface">{s.goals}</p>
              </>
            )}
            {s.bio && (
              <>
                <MLabel>{t('curatorStudentDetail.bio')}</MLabel>
                <p className="px-0.5 text-[13px] text-on-surface">{s.bio}</p>
              </>
            )}
          </div>
        )}
        <p className="px-0.5 pt-1 text-[11.5px] text-on-surface-variant">
          {t('curatorStudentDetail.joined')}: {fmtDate(s.created_at)}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {back}

      <Card className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Avatar name={name} src={s.avatar_url} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-2xl font-bold tracking-display text-on-surface">{name}</h1>
          {s.email ? (
            <p className="truncate text-sm text-on-surface-variant">{s.email}</p>
          ) : (
            <p className="text-sm text-on-surface-variant">
              {t('curatorStudents.studentCode', { code: (s.user_id ?? '').slice(0, 8) })}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {points.data ? (
              <Pill variant="success">{t('curatorStudents.points', { count: points.data.total_points })}</Pill>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/curator/students/${userId}/progress`}
            className="inline-flex items-center justify-center gap-1 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition hover:opacity-90"
          >
            <span className="material-symbols-outlined text-[18px]">trending_up</span>
            {t('studentProgress.open')}
          </Link>
          <Link
            to="/curator/chat"
            className="inline-flex items-center justify-center gap-1 rounded-pill bg-surface-container-highest px-4 py-2 text-sm font-semibold text-on-surface transition hover:opacity-90"
          >
            <span className="material-symbols-outlined text-[18px]">chat</span>
            {t('curatorStudentDetail.openChat')}
          </Link>
        </div>
      </Card>

      {/* Плиток две, а не три: «Уровень» убран совсем (решение владельца 12.09.2026). */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Stat
          label={t('curatorStudentDetail.points')}
          value={points.data ? String(points.data.total_points) : '—'}
          icon="stars"
        />
        <Stat label={t('curatorStudentDetail.lastActivity')} value={fmtDate(s.last_activity_at)} icon="schedule" />
      </div>

      {/* Сумма уже стоит плашкой у имени и плиткой выше — в заголовке разбивки
          она была бы третьей на одном экране. */}
      <StudentPointsCard studentId={userId!} showTotal={false} />

      {/* Откуда ученик входил (D41). Ручку сервер открыл и куратору — именно он
          первым замечает, что паролем пользуются двое. */}
      <StudentDevicesCard studentId={userId!} />

      {(s.goals || s.bio) && (
        <Card className="space-y-3">
          {s.goals && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                {t('curatorStudentDetail.goals')}
              </p>
              <p className="mt-1 text-on-surface">{s.goals}</p>
            </div>
          )}
          {s.bio && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                {t('curatorStudentDetail.bio')}
              </p>
              <p className="mt-1 text-on-surface">{s.bio}</p>
            </div>
          )}
        </Card>
      )}

      <Card variant="subtle" className="flex items-center gap-3 text-sm text-on-surface-variant">
        <span className="material-symbols-outlined text-[18px]">calendar_today</span>
        {t('curatorStudentDetail.joined')}: {fmtDate(s.created_at)}
      </Card>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <Card className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-container-highest text-primary">
        <span className="material-symbols-outlined">{icon}</span>
      </span>
      <div className="min-w-0">
        <p className="text-xs text-on-surface-variant">{label}</p>
        <p className="truncate font-semibold text-on-surface">{value}</p>
      </div>
    </Card>
  );
}
